import React, { useState, useEffect, useMemo } from 'react';
import { X, Check, Search, Wrench, Droplets, Layers, Plus, Pencil, Trash2, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import fetchWithAuth from '../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

const MATERIAL_TYPES = [
  'Sheet', 'Plate', 'Pipe', 'Tube', 'Round_Bar', 'Flat_Bar', 'Hex_Bar',
  'Square_Bar', 'Angle', 'Channel', 'Beam', 'Block',
];

const ProcessCheckItem = ({ process, isSelected, onToggle }) => (
  <button
    onClick={() => onToggle(process.process_code)}
    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
      isSelected ? 'bg-cyan-900/30 border border-cyan-600/30' : 'hover:bg-slate-800/50 border border-transparent'
    }`}
    data-testid={`tmpl-proc-${process.process_code}`}
  >
    <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 transition-colors ${
      isSelected ? 'bg-cyan-600 border-cyan-500 text-white' : 'bg-transparent border-slate-600'
    }`}>
      {isSelected && <Check className="w-2.5 h-2.5" />}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] text-slate-200 truncate">{process.process_name}</div>
      <div className="text-[9px] text-slate-500">{process.process_code}</div>
    </div>
    <span className="text-[9px] text-emerald-400/60 font-mono shrink-0">
      {process.cost_model?.rate > 0 ? `${process.cost_model.rate}/${process.cost_model.unit_label || 'pc'}` : ''}
    </span>
  </button>
);

export default function TemplateCreatorDrawer({ onClose, onCreated }) {
  const [fabProcesses, setFabProcesses] = useState([]);
  const [sftProcesses, setSftProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fabSearch, setFabSearch] = useState('');
  const [sftSearch, setSftSearch] = useState('');
  const [selectedFab, setSelectedFab] = useState(new Set());
  const [selectedSft, setSelectedSft] = useState(new Set());
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [materialTypes, setMaterialTypes] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [activePanel, setActivePanel] = useState(0); // 0=fab, 1=sft, 2=review, 3=manage

  // Manage tab state
  const [templates, setTemplates] = useState([]);
  const [tmplSearch, setTmplSearch] = useState('');
  const [tmplLoading, setTmplLoading] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create mode, string = edit mode

  useEffect(() => {
    (async () => {
      const tk = getToken();
      try {
        const [fabRes, sftRes] = await Promise.all([
          fetchWithAuth(`${API}/api/v1/process-library/processes?domain=fabrication&status=active&limit=200`, { headers: { Authorization: `Bearer ${tk}` } }),
          fetchWithAuth(`${API}/api/v1/process-library/processes?domain=surface_treatment&status=active&limit=200`, { headers: { Authorization: `Bearer ${tk}` } }),
        ]);
        if (fabRes.ok) { const d = await fabRes.json(); setFabProcesses(d.items || []); }
        if (sftRes.ok) { const d = await sftRes.json(); setSftProcesses(d.items || []); }
      } catch { /* ignore */ }
      setLoading(false);
    })();
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setTmplLoading(true);
    const tk = getToken();
    try {
      const res = await fetchWithAuth(`${API}/api/v1/process-library/templates?limit=200`, { headers: { Authorization: `Bearer ${tk}` } });
      if (res.ok) { const d = await res.json(); setTemplates(Array.isArray(d) ? d : d.items || []); }
    } catch {}
    setTmplLoading(false);
  };

  const toggleFab = (code) => setSelectedFab(prev => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const toggleSft = (code) => setSelectedSft(prev => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const toggleMaterial = (type) => setMaterialTypes(prev => { const n = new Set(prev); n.has(type) ? n.delete(type) : n.add(type); return n; });

  const filteredFab = useMemo(() => fabProcesses.filter(p =>
    p.process_name?.toLowerCase().includes(fabSearch.toLowerCase()) ||
    p.process_code?.toLowerCase().includes(fabSearch.toLowerCase())
  ), [fabProcesses, fabSearch]);

  const filteredSft = useMemo(() => sftProcesses.filter(p =>
    p.process_name?.toLowerCase().includes(sftSearch.toLowerCase()) ||
    p.process_code?.toLowerCase().includes(sftSearch.toLowerCase())
  ), [sftProcesses, sftSearch]);

  const groupByCategory = (items) => {
    const groups = {};
    items.forEach(p => { const c = p.process_category || 'Other'; if (!groups[c]) groups[c] = []; groups[c].push(p); });
    return groups;
  };

  const fabGrouped = useMemo(() => groupByCategory(filteredFab), [filteredFab]);
  const sftGrouped = useMemo(() => groupByCategory(filteredSft), [filteredSft]);
  const selectedFabList = fabProcesses.filter(p => selectedFab.has(p.process_code));
  const selectedSftList = sftProcesses.filter(p => selectedSft.has(p.process_code));

  const handleCreateOrUpdate = async () => {
    if (!templateName.trim()) { toast.error('Template name is required'); return; }
    if (selectedFab.size === 0 && selectedSft.size === 0) { toast.error('Select at least one process'); return; }
    setSaving(true);
    try {
      const tk = getToken();
      const body = {
        template_name: templateName.trim(),
        description: templateDesc.trim(),
        applicable_material_types: Array.from(materialTypes),
        fabrication_process_codes: Array.from(selectedFab),
        surface_treatment_codes: Array.from(selectedSft),
      };

      if (editingId) {
        // Update existing
        const res = await fetchWithAuth(`${API}/api/v1/process-library/templates/${editingId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({ ...body, fabrication_codes: body.fabrication_process_codes, surface_treatment_codes: body.surface_treatment_codes }),
        });
        if (res.ok) {
          toast.success(`Template "${templateName}" updated`);
          setEditingId(null);
          fetchTemplates();
          resetForm();
          setActivePanel(3);
        } else { const e = await res.json(); toast.error(e.detail || 'Update failed'); }
      } else {
        // Create new
        const res = await fetchWithAuth(`${API}/api/v1/process-library/templates`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const created = await res.json();
          toast.success(`Template "${templateName}" created`);
          onCreated?.(created);
          fetchTemplates();
          resetForm();
          setActivePanel(3);
        } else { const e = await res.json(); toast.error(e.detail || 'Create failed'); }
      }
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const resetForm = () => {
    setTemplateName(''); setTemplateDesc('');
    setSelectedFab(new Set()); setSelectedSft(new Set());
    setMaterialTypes(new Set()); setEditingId(null);
  };

  // Load a template into the editor tabs
  const loadTemplateForEdit = (t) => {
    setEditingId(t.template_id);
    setTemplateName(t.template_name || '');
    setTemplateDesc(t.description || '');
    setSelectedFab(new Set(t.fabrication_codes || []));
    setSelectedSft(new Set(t.surface_treatment_codes || []));
    setMaterialTypes(new Set(t.applicable_material_types || []));
    setActivePanel(0); // Go to Fabrication tab to start editing
  };

  const duplicateTemplate = (t) => {
    setEditingId(null); // Create mode
    setTemplateName(`${t.template_name} (Copy)`);
    setTemplateDesc(t.description || '');
    setSelectedFab(new Set(t.fabrication_codes || []));
    setSelectedSft(new Set(t.surface_treatment_codes || []));
    setMaterialTypes(new Set(t.applicable_material_types || []));
    setActivePanel(2); // Go to Review tab
    toast.success('Template duplicated — modify and save as new');
  };

  const deleteTemplate = async (templateId, name) => {
    if (!window.confirm(`Deprecate template "${name}"?`)) return;
    try {
      const tk = getToken();
      const res = await fetchWithAuth(`${API}/api/v1/process-library/templates/${templateId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${tk}` },
      });
      if (res.ok) { toast.success('Template deprecated'); fetchTemplates(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
  };

  const getProcessName = (code) => {
    const p = [...fabProcesses, ...sftProcesses].find(pr => pr.process_code === code);
    return p ? p.process_name : code;
  };

  const filteredTemplates = templates.filter(t =>
    t.status !== 'deprecated' && (!tmplSearch.trim() ||
    t.template_name?.toLowerCase().includes(tmplSearch.toLowerCase()) ||
    t.template_code?.toLowerCase().includes(tmplSearch.toLowerCase()))
  );

  const panels = [
    { label: 'Fabrication', icon: Wrench, color: 'orange', count: selectedFab.size },
    { label: 'Surface', icon: Droplets, color: 'violet', count: selectedSft.size },
    { label: editingId ? 'Save Changes' : 'Review', icon: Layers, color: 'cyan', count: null },
    { label: 'Manage', icon: Layers, color: 'emerald', count: filteredTemplates.length || null },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" data-testid="template-creator-drawer">
      <div className="w-[900px] max-h-[80vh] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            {editingId ? <Pencil className="w-4 h-4 text-amber-400" /> : <Plus className="w-4 h-4 text-cyan-400" />}
            <span className="text-sm font-semibold text-white">{editingId ? 'Edit Process Template' : 'Process Templates'}</span>
            {editingId && (
              <button onClick={() => { resetForm(); setActivePanel(3); }} className="text-[9px] text-slate-500 hover:text-slate-300 border border-slate-600 rounded px-2 py-0.5">Cancel Edit</button>
            )}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="template-modal-close"><X className="w-4 h-4" /></button>
        </div>

        {/* Panel Tabs */}
        <div className="flex border-b border-slate-700/50">
          {panels.map((p, i) => (
            <button key={i} onClick={() => setActivePanel(i)}
              className={`flex-1 py-2 text-[10px] uppercase tracking-wider font-semibold flex items-center justify-center gap-1.5 transition-colors border-b-2 ${
                activePanel === i
                  ? `text-${p.color}-400 border-${p.color}-500`
                  : 'text-slate-500 border-transparent hover:text-slate-300'
              }`}
              data-testid={`tmpl-panel-${i}`}
            >
              <p.icon className="w-3 h-3" />
              {p.label}
              {p.count !== null && p.count > 0 && (
                <span className={`px-1.5 py-0 rounded-full text-[8px] font-bold bg-${p.color}-600/30 text-${p.color}-300`}>{p.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Panel Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {loading ? (
            <div className="text-slate-500 text-center py-12">Loading processes...</div>
          ) : activePanel === 0 ? (
            <div className="h-full flex flex-col">
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <Input value={fabSearch} onChange={e => setFabSearch(e.target.value)}
                  placeholder="Search fabrication processes..."
                  className="pl-8 h-8 text-xs bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="text-[9px] text-slate-500 mb-2">{selectedFab.size} selected of {fabProcesses.length}</div>
              <div className="flex-1 overflow-y-auto space-y-2">
                {Object.entries(fabGrouped).map(([cat, items]) => (
                  <div key={cat}>
                    <div className="text-[9px] text-orange-400/70 font-semibold uppercase tracking-wider px-1 py-0.5 sticky top-0 bg-slate-900 z-10">{cat}</div>
                    {items.map(p => <ProcessCheckItem key={p.process_code} process={p} isSelected={selectedFab.has(p.process_code)} onToggle={toggleFab} />)}
                  </div>
                ))}
              </div>
            </div>
          ) : activePanel === 1 ? (
            <div className="h-full flex flex-col">
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <Input value={sftSearch} onChange={e => setSftSearch(e.target.value)}
                  placeholder="Search surface treatments..."
                  className="pl-8 h-8 text-xs bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="text-[9px] text-slate-500 mb-2">{selectedSft.size} selected of {sftProcesses.length}</div>
              <div className="flex-1 overflow-y-auto space-y-2">
                {Object.entries(sftGrouped).map(([cat, items]) => (
                  <div key={cat}>
                    <div className="text-[9px] text-violet-400/70 font-semibold uppercase tracking-wider px-1 py-0.5 sticky top-0 bg-slate-900 z-10">{cat}</div>
                    {items.map(p => <ProcessCheckItem key={p.process_code} process={p} isSelected={selectedSft.has(p.process_code)} onToggle={toggleSft} />)}
                  </div>
                ))}
              </div>
            </div>
          ) : activePanel === 2 ? (
            <div className="h-full overflow-y-auto space-y-4">
              <div className="space-y-2">
                <Label className="text-[10px] text-slate-400">Template Name *</Label>
                <Input value={templateName} onChange={e => setTemplateName(e.target.value)}
                  placeholder="e.g., SS Pipe Standard Processing"
                  className="h-8 text-xs bg-slate-800 border-slate-700 text-white"
                  data-testid="tmpl-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] text-slate-400">Description</Label>
                <Input value={templateDesc} onChange={e => setTemplateDesc(e.target.value)}
                  placeholder="Brief description"
                  className="h-8 text-xs bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] text-slate-400">Applicable Material Types</Label>
                <div className="flex flex-wrap gap-1.5">
                  {MATERIAL_TYPES.map(t => (
                    <button key={t} onClick={() => toggleMaterial(t)}
                      className={`px-2 py-1 rounded text-[10px] transition-colors ${
                        materialTypes.has(t) ? 'bg-cyan-600/30 text-cyan-300 border border-cyan-500/40' : 'bg-slate-800 text-slate-500 border border-slate-700 hover:text-slate-300'
                      }`} data-testid={`tmpl-mat-${t}`}>{t.replace('_', ' ')}</button>
                  ))}
                </div>
              </div>
              <div className="border border-orange-700/30 rounded-lg p-3 bg-orange-900/10">
                <div className="text-[10px] text-orange-400 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Wrench className="w-3 h-3" /> Fabrication ({selectedFab.size})
                </div>
                {selectedFabList.length === 0 ? <div className="text-[10px] text-slate-500 italic">None selected</div> : (
                  <div className="space-y-1">{selectedFabList.map((p, i) => (
                    <div key={p.process_code} className="flex items-center gap-2 text-[10px]">
                      <span className="text-slate-600 font-mono w-4">{i + 1}</span>
                      <span className="text-slate-200">{p.process_name}</span>
                      <span className="text-slate-600 ml-auto font-mono">{p.process_code}</span>
                    </div>
                  ))}</div>
                )}
              </div>
              <div className="border border-violet-700/30 rounded-lg p-3 bg-violet-900/10">
                <div className="text-[10px] text-violet-400 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Droplets className="w-3 h-3" /> Surface Treatment ({selectedSft.size})
                </div>
                {selectedSftList.length === 0 ? <div className="text-[10px] text-slate-500 italic">None selected</div> : (
                  <div className="space-y-1">{selectedSftList.map((p, i) => (
                    <div key={p.process_code} className="flex items-center gap-2 text-[10px]">
                      <span className="text-slate-600 font-mono w-4">{i + 1}</span>
                      <span className="text-slate-200">{p.process_name}</span>
                      <span className="text-slate-600 ml-auto font-mono">{p.process_code}</span>
                    </div>
                  ))}</div>
                )}
              </div>
            </div>
          ) : (
            /* Panel 4: Manage Templates */
            <div className="h-full flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                  <Input value={tmplSearch} onChange={e => setTmplSearch(e.target.value)}
                    placeholder="Search templates..." className="pl-8 h-8 text-xs bg-slate-800 border-slate-700 text-white"
                    data-testid="manage-tmpl-search" />
                </div>
                <Button size="sm" className="h-8 text-[10px] bg-cyan-600 hover:bg-cyan-500" onClick={() => { resetForm(); setActivePanel(0); }}
                  data-testid="manage-new-tmpl-btn">
                  <Plus className="w-3 h-3 mr-1" /> New Template
                </Button>
              </div>
              {tmplLoading ? (
                <div className="text-slate-500 text-center py-8 text-xs">Loading...</div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-slate-500 text-center py-8 text-xs">No templates found</div>
              ) : (
                <div className="flex-1 overflow-y-auto space-y-2">
                  {filteredTemplates.map(t => (
                    <div key={t.template_id} className="border border-slate-700/40 rounded-lg p-3 bg-slate-800/30 hover:border-slate-600/60 transition-colors"
                      data-testid={`manage-tmpl-${t.template_id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-white">{t.template_name}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{t.template_code}</div>
                          {t.description && <div className="text-[10px] text-slate-400 mt-0.5">{t.description}</div>}
                          <div className="flex gap-2 mt-1 text-[9px] text-slate-500 flex-wrap">
                            {(t.fabrication_codes || []).map(c => <span key={c} className="bg-orange-500/10 text-orange-400 px-1 rounded">{getProcessName(c)}</span>)}
                            {(t.surface_treatment_codes || []).map(c => <span key={c} className="bg-violet-500/10 text-violet-400 px-1 rounded">{getProcessName(c)}</span>)}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-2">
                          <button onClick={() => loadTemplateForEdit(t)} className="p-1.5 rounded text-slate-500 hover:text-cyan-400 hover:bg-slate-700/50" title="Edit"
                            data-testid={`manage-tmpl-edit-${t.template_id}`}><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => duplicateTemplate(t)} className="p-1.5 rounded text-slate-500 hover:text-emerald-400 hover:bg-emerald-900/20" title="Duplicate"
                            data-testid={`manage-tmpl-dup-${t.template_id}`}><Copy className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deleteTemplate(t.template_id, t.template_name)} className="p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-900/20" title="Delete"
                            data-testid={`manage-tmpl-del-${t.template_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700/50 bg-slate-900/80">
          <div className="text-[10px] text-slate-500">
            {activePanel < 3 ? `${selectedFab.size} fabrication + ${selectedSft.size} surface treatment` : `${filteredTemplates.length} templates`}
          </div>
          <div className="flex gap-2">
            {activePanel < 2 && (
              <Button onClick={() => setActivePanel(activePanel + 1)}
                className="h-7 text-[10px] bg-slate-700 hover:bg-slate-600 text-white">Next</Button>
            )}
            {activePanel === 2 && (
              <Button onClick={handleCreateOrUpdate} disabled={saving || !templateName.trim()}
                className="h-7 text-[10px] bg-cyan-600 hover:bg-cyan-500 text-white"
                data-testid="tmpl-create-btn">
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Template'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
